import { useState, useEffect, useRef, useCallback, useMemo, lazy, Suspense } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  ArrowPathIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Squares2X2Icon,
  ListBulletIcon,
  TrashIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { useNode } from '../contexts/NodeContext';
import { useAuth } from '../contexts/AuthContext';
import {
  getNodeAlertNames,
  getNodeAlerts,
  getNodeAlertStats,
  acknowledgeNodeAlert,
  unacknowledgeNodeAlert,
  assignNodeAlert,
  resolveNodeAlert,
  cancelNodeAlert,
  deleteNodeAlert,
  getNodeCameras,
  getNodeUsers,
  getNodeModelEventImage,
} from '../services/api.service';
import { tableToModel } from '../api/hexAlerts.core';
import {
  AlertListCards,
  AlertListFilters,
  AlertListTable,
  Avatar,
  Modal,
  PageTabs,
  RecordPager,
  AlertFullPageButton,
  SegmentedToggle,
  alertName,
  alertRangeFrom,
  PageTitle,
  PageWithFilters,
  FilterColumn,
  useFilterColumn,
  initialsOf,
  type AlertRangeKey,
} from '@trinetra/shared';

/**
 * The alert name, the person bubble and its initials now live in @trinetra/shared — the alert
 * list, the alert detail page and the local node's copies of both have to spell a person and
 * an alert the same way. Re-exported because AlertDetailPage has always imported them here.
 */
export { alertName, Avatar, initialsOf };

/**
 * The alert record the list opens in a modal.
 *
 * A dynamic import rather than a static one for exactly one reason: AlertDetailPage already
 * imports this module's own parts (Field, ActionModal, alertName…), and a static edge back
 * would close that into a cycle. It buys no code-splitting — the routed page imports the same
 * module statically — so treat it as a dependency-direction fix, not a performance one.
 */
const AlertDetailView = lazy(() =>
  import('./AlertDetailPage').then((m) => ({ default: m.AlertDetailView })),
);

const STATUSES = ['new', 'acknowledged', 'assigned', 'resolved', 'cancelled'] as const;
type Status = (typeof STATUSES)[number];

/** Tab labels, so the strip reads "Acknowledged" rather than a CSS-capitalised "acknowledged". */
const STATUS_LABEL: Record<Status, string> = {
  new: 'New',
  acknowledged: 'Acknowledged',
  assigned: 'Assigned',
  resolved: 'Resolved',
  cancelled: 'Cancelled',
};

const DEFAULT_PAGE_SIZE = 25;

export const badgeCls = 'px-1.5 py-0.5 text-[10px] font-bold uppercase rounded';

export function fmtTime(ts?: number) {
  if (!ts) return '—';
  let ms = Number(ts);
  if (ms < 1e12) ms *= 1000;
  const d = new Date(ms);
  return d.toLocaleDateString([], { day: '2-digit', month: 'short' }) + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

export default function AlertsPage() {
  const { selectedNode } = useNode();
  const { user, hasPermission } = useAuth();
  const canManageAlerts = hasPermission('alerts.edit'); // backend also enforces
  /* A SEPARATE grant from alerts.edit — working an alert and erasing it are different powers.
     The cloud proxy gates DELETE on this one too, so withholding it here is not the only
     thing standing between a viewer and the row. */
  const canDeleteAlerts = hasPermission('alerts.delete');
  const nodeId = selectedNode?.nodeId || '';
  const uid = user?.id || null;
  const uname = user?.name || user?.email || null;

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const cameraId = searchParams.get('camera_id') || '';

  const [alerts, setAlerts] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  /**
   * Cards or rows — two presentations of the SAME page of alerts (see AlertListCards).
   *
   * Cards by default: half of triage is recognising the scene, and a queue of "Person
   * Detected · Main Gate" rows all read alike. The table is one click away and remembered,
   * because the operator who works twenty-five at a time wants the density.
   */
  const [view, setView] = useState<'cards' | 'table'>(
    () => (localStorage.getItem('alerts.view') === 'table' ? 'table' : 'cards'),
  );
  useEffect(() => { localStorage.setItem('alerts.view', view); }, [view]);

  const [statusFilter, setStatusFilter] = useState<Status | 'all'>('all');
  const [severity, setSeverity] = useState('');
  /**
   * The alert's NAME, as the card shows it — the same axis the alert board filters on.
   *
   * Called `typeFilter`, not `alertName`: this module already imports `alertName`, the helper
   * that DERIVES the displayed name from a row, and a state variable of that name shadows it
   * inside the component — where every call to it then fails at runtime, silently, because
   * this app's build does not typecheck.
   */
  const [typeFilter, setTypeFilter] = useState('');
  const [typeOptions, setTypeOptions] = useState<{ name: string; count: number }[]>([]);
  /**
   * Two search states, deliberately: `searchInput` is what's in the box and `search` is what
   * the node has been asked for. The box used to filter only on Enter, which reads as a dead
   * control; debouncing the copy that drives the request gives it live feedback without a
   * round trip through the tunnel per keystroke.
   */
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  /**
   * Defaults to "all time" rather than the last 24 hours: a site whose last alert is three
   * days old would otherwise open to an empty page that looks like a broken tunnel, not a
   * quiet site. The window is one dropdown away for the operators who want it.
   */
  const [range, setRange] = useState<AlertRangeKey>('all');
  // Paging is server-side, so the page size is part of the request — changing it refetches.
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  /**
   * Per-status totals behind the tab strip, from the node's /alerts/stats under the SAME
   * filters the list runs. Empty until the first response (and after a failure), which
   * renders the tabs label-only rather than with a row of confident zeroes.
   */
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [actionFor, setActionFor] = useState<{ alert: any; kind: 'resolve' | 'cancel' } | null>(null);
  /* Its own dialog rather than a third `kind` of the note modal: no text to type, the alert's
     name shown back so the operator can see what they are erasing, and a red confirm. */
  const [deleteFor, setDeleteFor] = useState<any | null>(null);
  const [deleting, setDeleting] = useState(false);
  /**
   * The row being read, as an INDEX into the page of alerts rather than an alert.
   *
   * Working alerts is a sweep down the list, so the record opens over it — keeping the filters,
   * the scroll position and the page — and the arrow keys move to the next one without closing.
   * `/alerts/:id` still exists for links out of here; the "Full page" button goes there.
   */
  const [viewingIndex, setViewingIndex] = useState<number | null>(null);
  const viewing = viewingIndex !== null ? alerts[viewingIndex] : null;
  const [assignFor, setAssignFor] = useState<any | null>(null);
  const [cameras, setCameras] = useState<{ id: string; name?: string }[]>([]);
  const cameraMap = useMemo(
    () => Object.fromEntries(cameras.map((c) => [c.id, c.name || c.id])),
    [cameras],
  );
  const [users, setUsers] = useState<any[]>([]);
  const userMap = useMemo(
    () => Object.fromEntries(users.map((u) => [u.id, u.full_name || u.name || u.email || u.id])),
    [users],
  );

  useEffect(() => {
    if (!nodeId) return;
    getNodeCameras(nodeId)
      .then((cams: any) => {
        const arr = Array.isArray(cams) ? cams : cams?.data || [];
        setCameras(arr.map((c: any) => ({ id: c.id, name: c.name })));
      })
      .catch(() => {});
    // Node users (local + provisioned cloud users with access) for the assign dropdown + name resolution.
    getNodeUsers(nodeId)
      .then((list: any[]) => setUsers(Array.isArray(list) ? list : []))
      .catch(() => setUsers([]));
  }, [nodeId]);

  // Everything except the status tab — the list sends this plus its status, the tab counts
  // send it WITHOUT one, which is what makes a tab's number the size of the queue behind it.
  const filterParams = useMemo(() => {
    const params: any = {};
    if (severity) params.severity = severity;
    if (search) params.search = search;
    if (cameraId) params.camera_id = cameraId;
    const from = alertRangeFrom(range);
    if (from) params.from = from;
    return params;
  }, [severity, search, cameraId, range]);

  /**
   * The list's params. `alert_name` is kept OUT of `filterParams`: that object also feeds the
   * status tab counts and the name options, and a tab reading "Open 3" under a type filter
   * would describe a queue the operator cannot see the rest of.
   */
  const listParams = useMemo(
    () => (typeFilter ? { ...filterParams, alert_name: typeFilter } : filterParams),
    [filterParams, typeFilter],
  );

  const fetchAlerts = useCallback(async () => {
    if (!nodeId) { setAlerts([]); setLoading(false); return; }
    setLoading(true);
    try {
      const params: any = { ...listParams, page, limit: pageSize, sort_by: 'event_timestamp', sort_order: 'desc' };
      if (statusFilter !== 'all') params.status = statusFilter;
      const res: any = await getNodeAlerts(nodeId, params);
      const rows = res?.data ?? res ?? [];
      setAlerts(Array.isArray(rows) ? rows : []);
      setTotal(res?.pagination?.total ?? rows.length ?? 0);
    } catch (err) {
      console.error('Failed to fetch alerts:', err);
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  }, [nodeId, listParams, page, pageSize, statusFilter]);

  const fetchCounts = useCallback(async () => {
    if (!nodeId) { setCounts({}); return; }
    try {
      const stats: any = await getNodeAlertStats(nodeId, filterParams);
      // A status with nothing behind it is absent from by_status — seed the zeroes so the
      // tab shows "0" rather than dropping its badge and shifting the strip.
      const zeroes = Object.fromEntries(STATUSES.map((s) => [s, 0]));
      const byStatus = Object.fromEntries((stats?.by_status || []).map((r: any) => [r.status, Number(r.count)]));
      setCounts({ ...zeroes, ...byStatus, all: Number(stats?.total ?? 0) });
    } catch {
      setCounts({});
    }
  }, [nodeId, filterParams]);

  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);
  useEffect(() => { fetchCounts(); }, [fetchCounts]);

  /**
   * The alert's picture, streamed through the node tunnel.
   *
   * A node's `image_urls` are absolute URLs INSIDE that node — the cloud browser can't reach
   * them, so the bytes come back as a blob from the same proxy the alert record uses
   * (`getNodeModelEventImage`, by model + event id + image type). The list only asks for ONE
   * frame per alert, `fullFrame` where the model produced one, and the shared resolver holds
   * the request until the card is actually on screen: a page of twenty-five is twenty-five
   * round trips over a link that may be a long way away.
   *
   * A tunnel 404 or timeout still resolves as a (JSON/text) Blob, so only real images count —
   * otherwise the card would render an <img> of an error message.
   */
  const loadThumbnail = useCallback(
    async (alert: any): Promise<string | null> => {
      const model = tableToModel(alert?.source_table);
      if (!nodeId || !model || !alert?.event_id) return null;
      const urls = alert?.image_urls && typeof alert.image_urls === 'object' ? alert.image_urls : {};
      const type = urls.fullFrame ? 'fullFrame' : Object.keys(urls).find((k) => urls[k]);
      if (!type) return null;
      try {
        const blob: any = await getNodeModelEventImage(nodeId, model, alert.event_id, type);
        if (!blob || (blob.type && !blob.type.startsWith('image/'))) return null;
        return URL.createObjectURL(blob);
      } catch {
        return null;
      }
    },
    [nodeId],
  );

  /**
   * Switching site starts this page over.
   *
   * The list refetches on its own (every fetch keys off `nodeId`), but the things POINTING at
   * rows do not: `?camera_id=` names a camera the new site hasn't got, so the queue comes back
   * empty and reads as a quiet site rather than as a filter that can never match; the open
   * record is an INDEX into a page of alerts that has just been replaced; and page 4 of the old
   * site's queue is not where the new one should open.
   */
  const firstNodeRef = useRef(true);
  useEffect(() => {
    if (firstNodeRef.current) { firstNodeRef.current = false; return; }
    setPage(1);
    setViewingIndex(null);
    setActionFor(null);
    setAssignFor(null);
    setDeleteFor(null);
    if (searchParams.get('camera_id')) {
      const next = new URLSearchParams(searchParams);
      next.delete('camera_id');
      setSearchParams(next, { replace: true });
    }
    // searchParams is read, not tracked: this must fire on a site change and nothing else.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeId]);

  // Debounced: the box filters as you type without a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput.trim()); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const setCameraFilter = (id: string) => {
    if (id) searchParams.set('camera_id', id); else searchParams.delete('camera_id');
    setSearchParams(searchParams, { replace: true });
    setPage(1);
  };

  const clearFilters = () => {
    setSeverity(''); setTypeFilter(''); setSearchInput(''); setSearch(''); setRange('all'); setPage(1);
    if (cameraId) { searchParams.delete('camera_id'); setSearchParams(searchParams, { replace: true }); }
  };

  /** The names to offer, under everything EXCEPT the name filter itself. */
  const fetchAlertNames = useCallback(async () => {
    if (!nodeId) { setTypeOptions([]); return; }
    try {
      setTypeOptions(await getNodeAlertNames(nodeId, filterParams));
    } catch {
      // An older node has no such endpoint; the rest of the bar keeps working.
      setTypeOptions([]);
    }
  }, [nodeId, filterParams]);

  useEffect(() => { void fetchAlertNames(); }, [fetchAlertNames]);

  // A name whose last alert has just aged out of the window would otherwise leave the list
  // filtered by something no longer on offer.
  useEffect(() => {
    if (typeFilter && typeOptions.length && !typeOptions.some((n) => n.name === typeFilter)) {
      setTypeFilter('');
      setPage(1);
    }
  }, [typeOptions, typeFilter]);

  const refresh = () => { fetchAlerts(); fetchCounts(); fetchAlertNames(); };

  // A lifecycle change moves the alert between tabs, so the counts have to move with it.
  const apply = (id: string, updated: any) => {
    setAlerts((prev) => prev.map((a) => (a.id === id ? { ...a, ...updated } : a)));
    fetchCounts();
  };

  const tabs = useMemo(() => ([
    { key: 'all', label: 'All', count: counts.all ?? null },
    ...STATUSES.map((s) => ({ key: s, label: STATUS_LABEL[s], count: counts[s] ?? null })),
  ]), [counts]);

  const doAck = async (a: any) => { try { apply(a.id, await acknowledgeNodeAlert(nodeId, a.id, uid, uname)); } catch (e) { console.error(e); } };
  const doUnack = async (a: any) => { try { apply(a.id, await unacknowledgeNodeAlert(nodeId, a.id, uid, uname)); } catch (e) { console.error(e); } };
  const doAssignMe = async (a: any) => { if (!uid) return; try { apply(a.id, await assignNodeAlert(nodeId, a.id, uid, uid, uname, uname)); } catch (e) { console.error(e); } };
  const doAssign = async (a: any, assigneeId: string, assigneeName: string) => { try { apply(a.id, await assignNodeAlert(nodeId, a.id, assigneeId, uid, assigneeName, uname)); } catch (e) { console.error(e); } };

  /**
   * Erase the alert on the node. Not a lifecycle move — the row and its action history go.
   *
   * REFETCHES rather than splicing the row out: paging is server-side (and, here, a tunnel
   * away), so a page that quietly loses a row disagrees with its own total and never pulls up
   * the row that should have moved in from the next page. Deleting the last row of a page past
   * the first leaves that page nonexistent, so step back one and let the effect fetch it.
   */
  const doDelete = async (a: any) => {
    setDeleting(true);
    try {
      await deleteNodeAlert(nodeId, a.id);
      if (viewing?.id === a.id) setViewingIndex(null);
      if (alerts.length === 1 && page > 1) { setPage((p) => p - 1); fetchCounts(); }
      else refresh();
      setDeleteFor(null);
    } catch (e) {
      console.error('Failed to delete alert:', e);
    } finally {
      setDeleting(false);
    }
  };
  const doResolve = async (a: any, note: string) => { try { apply(a.id, await resolveNodeAlert(nodeId, a.id, uid, note, uname)); } catch (e) { console.error(e); } };
  const doCancel = async (a: any, reason: string) => { try { apply(a.id, await cancelNodeAlert(nodeId, a.id, uid, reason, uname)); } catch (e) { console.error(e); } };

  const filterCol = useFilterColumn('alerts-list');
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  const activeFilters = [search, severity, typeFilter, cameraId, range !== 'all'].filter(Boolean).length;

  return (
    <>
      <PageTitle
        title="Alert List"
        subtitle={<>{total > 0 && `${total} alerts · `}Acknowledge, assign and resolve alerts for <span className="font-medium text-foreground">{selectedNode?.name || nodeId || '—'}</span></>}
      >
        <button
          onClick={refresh}
          className="flex h-8 w-8 items-center justify-center rounded-lg border border-border text-muted-foreground hover:bg-muted transition-colors"
          title="Refresh"
        >
          <ArrowPathIcon className="h-4 w-4" />
        </button>
      </PageTitle>

      <PageWithFilters
        filters={(
          <FilterColumn
            open={filterCol.open}
            onToggle={filterCol.toggle}
            label="Alert filters"
            activeCount={activeFilters}
            onClearAll={clearFilters}
          >
            <AlertListFilters
              variant="column"
              search={searchInput}
              onSearchChange={setSearchInput}
              severity={severity}
              onSeverityChange={(v) => { setSeverity(v); setPage(1); }}
              alertName={typeFilter}
              onAlertNameChange={(v) => { setTypeFilter(v); setPage(1); }}
              alertNames={typeOptions}
              cameraId={cameraId}
              onCameraChange={setCameraFilter}
              cameras={cameras}
              range={range}
              onRangeChange={(v) => { setRange(v); setPage(1); }}
              pageSize={pageSize}
              onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
            />
          </FilterColumn>
        )}
      >
      {/* ── No site selected ── */}
      {!nodeId ? (
        <div className="rounded-xl border border-border bg-card shadow-card py-16 text-center text-muted-foreground text-sm">
          Select a site from the sidebar to view its alerts.
        </div>
      ) : (
        /* ── Status tabs + table ──
           One card: the tabs are a view onto the table under them, not a separate control, and
           a gap between the two made them read as a second filter bar. */
        <div className="rounded-xl border border-border bg-card shadow-card overflow-hidden">
          {/* The view switch rides on the tab strip's own line — it belongs to the list under
              it, and a second toolbar row for one control would push the alerts down. */}
          <div className="flex items-center justify-between gap-3 pr-3">
            <PageTabs
              tabs={tabs}
              active={statusFilter}
              onChange={(key) => { setStatusFilter(key as Status | 'all'); setPage(1); }}
              className="min-w-0 flex-1 px-2"
            />
            <SegmentedToggle<'cards' | 'table'>
              aria-label="Alert list view"
              value={view}
              onChange={setView}
              options={[
                { key: 'cards', label: 'Cards', icon: Squares2X2Icon, title: 'Cards — the alert’s own frame' },
                { key: 'table', label: 'Table', icon: ListBulletIcon, title: 'Table — more alerts per screen' },
              ]}
            />
          </div>

          {/* Both views take the same props: same page, same actions, same record opened. */}
          {(() => {
            const listProps = {
              alerts,
              loading,
              cameraMap,
              userMap,
              canManage: canManageAlerts,
              canDelete: canDeleteAlerts,
              loadThumbnail,
              onOpen: setViewingIndex,
              onAcknowledge: doAck,
              onAssign: setAssignFor,
              onResolve: (a: any) => setActionFor({ alert: a, kind: 'resolve' as const }),
              onCancel: (a: any) => setActionFor({ alert: a, kind: 'cancel' as const }),
              onReopen: doUnack,
              onDelete: (a: any) => setDeleteFor(a),
              emptyHint:
                statusFilter !== 'all' || severity || typeFilter || search || cameraId || range !== 'all'
                  ? 'Try another status tab, or clear the filters.'
                  : 'Alerts appear here as soon as this site raises one.',
            };
            return view === 'cards' ? <AlertListCards {...listProps} /> : <AlertListTable {...listProps} />;
          })()}

          {/* Pagination */}
          {!loading && alerts.length > 0 && (
            <div className="flex items-center justify-between border-t border-border px-4 py-3">
              <span className="text-[12px] text-muted-foreground">
                {/* Echoing the size the request actually used makes a mismatch with the
                    filter-bar dropdown obvious instead of something you must count rows for. */}
                {from}–{to} of {total} · Page {page} of {totalPages} · {pageSize} per page
              </span>
              <div className="flex items-center gap-2">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                  className="flex h-8 items-center gap-1 rounded-lg border border-border px-3 text-[12px] text-foreground hover:bg-muted disabled:opacity-30 transition-colors"
                >
                  <ChevronLeftIcon className="h-3.5 w-3.5" /> Prev
                </button>
                <button
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className="flex h-8 items-center gap-1 rounded-lg border border-border px-3 text-[12px] text-foreground hover:bg-muted disabled:opacity-30 transition-colors"
                >
                  Next <ChevronRightIcon className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      </PageWithFilters>

      {assignFor && (
        <AssignModal
          users={users}
          currentUser={{ id: uid, name: uname }}
          onClose={() => setAssignFor(null)}
          onConfirm={(assigneeId: string, assigneeName: string) => {
            doAssign(assignFor, assigneeId, assigneeName);
            setAssignFor(null);
          }}
        />
      )}
      {/* ── Alert Detail Modal ──
          The common Modal shell at full width, with no header of its own: the record draws one
          (severity, status, name, camera, time — things only the loaded alert knows) and carries
          the pager, "Full page" and close in it. Left/right step through the page's rows; Escape
          closes. Closing refetches, so a status changed in here shows in the row behind it. */}
      <Modal
        open={!!viewing}
        onClose={() => { setViewingIndex(null); refresh(); }}
        size="full"
        ariaLabel={viewing ? `${alertName(viewing)} — alert details` : 'Alert details'}
      >
        {/* Keyed on the alert id so stepping to the next row refetches instead of showing the
            previous alert's data while the new one loads. */}
        {viewing && (
          <Suspense fallback={<div className="p-6"><div className="h-64 animate-pulse rounded-lg bg-muted" /></div>}>
            <AlertDetailView
              key={viewing.id}
              alertId={viewing.id}
              showTitle={false}
              onClose={() => { setViewingIndex(null); refresh(); }}
              headerActions={(
                <>
                  <RecordPager
                    variant="boxed"
                    index={viewingIndex ?? 0}
                    count={alerts.length}
                    onIndexChange={setViewingIndex}
                    noun="alerts"
                  />
                  <AlertFullPageButton onClick={() => navigate(`/alerts/${viewing.id}`)} />
                </>
              )}
            />
          </Suspense>
        )}
      </Modal>

      {actionFor && (
        <ActionModal
          kind={actionFor.kind}
          onClose={() => setActionFor(null)}
          onConfirm={(value: string) => {
            const { alert, kind } = actionFor;
            if (kind === 'resolve') doResolve(alert, value); else doCancel(alert, value);
            setActionFor(null);
          }}
        />
      )}

      {deleteFor && (
        <DeleteAlertModal
          alert={deleteFor}
          name={alertName(deleteFor)}
          camera={cameraMap[deleteFor.camera_id] || deleteFor.camera_id}
          busy={deleting}
          onClose={() => setDeleteFor(null)}
          onConfirm={() => doDelete(deleteFor)}
        />
      )}
    </>
  );
}

/**
 * The confirm for a delete — deliberately NOT the note dialog with a third mode.
 *
 * It states WHAT is about to go (name, camera, when), because the bin was clicked on one row
 * of twenty-five identical-looking ones and "Are you sure?" cannot be answered without
 * knowing which. It says the deletion is permanent, and names the two things that are NOT
 * this — Resolve and Cancel — since an operator reaching for "make this go away" usually
 * wants one of those and keeps the record.
 *
 * `busy` disables both buttons while the DELETE is in flight, which over a tunnel is long
 * enough for an impatient second click to reach an id that has already gone.
 */
export function DeleteAlertModal({ alert, name, camera, busy, onClose, onConfirm }: any) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={busy ? undefined : onClose}>
      <div className="w-full max-w-md rounded-2xl border border-border bg-card shadow-2xl p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-red-50 dark:bg-red-950">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-600 dark:text-red-400" />
          </span>
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-foreground">Delete this alert?</h2>
            <p className="mt-1 text-[13px] text-muted-foreground">
              It is removed permanently from the node, together with its action history. This cannot be undone.
            </p>
          </div>
        </div>

        <div className="mt-4 rounded-lg border border-border bg-muted px-3 py-2.5">
          <p className="truncate text-[13px] font-semibold text-foreground" title={name}>{name}</p>
          <p className="mt-0.5 truncate text-[12px] text-muted-foreground">
            {[camera, alert?.event_timestamp ? fmtTime(alert.event_timestamp) : null].filter(Boolean).join(' · ') || '—'}
          </p>
        </div>

        <p className="mt-3 text-[12px] text-muted-foreground">
          To close an alert but keep the record, use <span className="font-medium text-foreground">Resolve</span> or{' '}
          <span className="font-medium text-foreground">Cancel</span> instead.
        </p>

        <div className="flex justify-end gap-2 mt-5">
          <button
            onClick={onClose}
            disabled={busy}
            className="h-9 px-4 rounded-lg border border-border text-[13px] text-foreground hover:bg-muted disabled:opacity-50 transition-colors"
          >
            Keep it
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            className="flex h-9 items-center gap-1.5 px-4 rounded-lg bg-red-600 text-white text-[13px] font-medium hover:bg-red-700 disabled:opacity-60 transition-colors"
          >
            <TrashIcon className="h-4 w-4" />
            {busy ? 'Deleting…' : 'Delete permanently'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Assign dialog: quick "Assign to me" + a dropdown of every node user (local + cloud-with-access). */
export function AssignModal({ users, currentUser, onClose, onConfirm }: any) {
  const [sel, setSel] = useState('');
  const nameFor = (u: any) => u.full_name || u.name || u.email || u.id;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl border border-border bg-card shadow-2xl p-6" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-semibold text-foreground mb-3">Assign alert</h2>
        {currentUser?.id && (
          <button
            onClick={() => onConfirm(currentUser.id, currentUser.name || 'Me')}
            className="mb-3 w-full rounded-lg border border-border px-3 py-2 text-left text-[13px] text-foreground hover:bg-muted transition-colors"
          >
            Assign to me{currentUser.name ? ` (${currentUser.name})` : ''}
          </button>
        )}
        <label className="block text-[12px] font-medium text-muted-foreground mb-1">Assign to user</label>
        <select
          value={sel}
          onChange={(e) => setSel(e.target.value)}
          className="w-full h-9 rounded-lg border border-border bg-background px-3 text-[13px] text-foreground focus:border-brand focus:outline-none"
        >
          <option value="">Select a user…</option>
          {users.map((u: any) => (
            <option key={u.id} value={u.id}>
              {nameFor(u)}{u.user_type === 'cloud' ? ' · cloud' : ''}
            </option>
          ))}
        </select>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="h-9 px-4 rounded-lg border border-border text-[13px] text-foreground hover:bg-muted transition-colors">
            Cancel
          </button>
          <button
            onClick={() => {
              const u = users.find((x: any) => x.id === sel);
              if (u) onConfirm(u.id, nameFor(u));
            }}
            disabled={!sel}
            className="h-9 px-4 rounded-lg bg-brand text-white text-[13px] font-medium hover:bg-brand/90 disabled:opacity-40 transition-colors"
          >
            Assign
          </button>
        </div>
      </div>
    </div>
  );
}

export function ActionModal({ kind, onClose, onConfirm }: any) {
  const [text, setText] = useState('');
  const title = kind === 'resolve' ? 'Resolve alert' : 'Cancel alert (false positive)';
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl border border-border bg-card shadow-2xl p-6" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-semibold text-foreground mb-3">{title}</h2>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={3}
          placeholder={kind === 'resolve' ? 'Resolution note (optional)' : 'Reason (optional)'}
          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-[13px] text-foreground placeholder:text-muted-foreground focus:border-brand focus:outline-none"
          autoFocus
        />
        <div className="flex justify-end gap-2 mt-4">
          <button
            onClick={onClose}
            className="h-9 px-4 rounded-lg border border-border text-[13px] text-foreground hover:bg-muted transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(text)}
            className="h-9 px-4 rounded-lg bg-brand text-white text-[13px] font-medium hover:bg-brand/90 transition-colors"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}
